import { getCookieConsentFromHeader, getCookieConsentSourceFromHeader } from "./cookieConsent";

export const GOOGLE_ADS_CLICK_ID_QUERY_PARAMS = ["gclid", "gbraid"] as const;
export type GoogleAdsClickIdType = (typeof GOOGLE_ADS_CLICK_ID_QUERY_PARAMS)[number];
export type GoogleAdsClickId = {
  type: GoogleAdsClickIdType;
  value: string;
};
export const GOOGLE_ADS_CLICK_ID_STORAGE_KEYS: Record<GoogleAdsClickIdType, string> = {
  gclid: "tapestry:googleAds:gclid",
  gbraid: "tapestry:googleAds:gbraid",
};
export const GOOGLE_ADS_CLICK_ID_COOKIE_MAX_AGE_DAYS = 1;
const INTERNAL_URL_ORIGIN = "https://ads.openai.com";
const MAX_GOOGLE_ADS_CLICK_ID_LENGTH = 256;
const GOOGLE_ADS_ROLLOUT_COUNTRIES = new Set(["AU", "BR", "CA", "GB", "MX", "NZ", "US"]);

export function isGoogleAdsRolloutCountry(countryCode: string | undefined): boolean {
  return countryCode != null && GOOGLE_ADS_ROLLOUT_COUNTRIES.has(countryCode.toUpperCase());
}

export function isGoogleAdsRolloutCountryHeader(
  countryHeader: string | string[] | null | undefined,
): boolean {
  const countryCode = Array.isArray(countryHeader) ? countryHeader[0] : countryHeader;
  return isGoogleAdsRolloutCountry(countryCode?.trim() || undefined);
}

export function getGoogleAdsClickId(searchParams: URLSearchParams): GoogleAdsClickId | undefined {
  for (const type of GOOGLE_ADS_CLICK_ID_QUERY_PARAMS) {
    const values = searchParams.getAll(type);
    if (values.length === 0) continue;
    if (values.length !== 1) return undefined;

    const rawValue = values[0];
    const value = rawValue?.trim();
    if (!value || value !== rawValue || !isValidGoogleAdsClickId(value)) {
      return undefined;
    }
    return { type, value };
  }

  return undefined;
}

export function appendGoogleAdsClickIdToPath(
  path: string,
  clickId: GoogleAdsClickId | undefined,
): string {
  try {
    const url = new URL(path, INTERNAL_URL_ORIGIN);
    if (url.origin !== INTERNAL_URL_ORIGIN) return path;

    removeGoogleAdsClickIdsFromSearchParams(url.searchParams);
    if (clickId && isValidGoogleAdsClickId(clickId.value)) {
      url.searchParams.set(clickId.type, clickId.value);
    }
    return toInternalPath(url);
  } catch {
    return path;
  }
}

export function removeGoogleAdsClickIdsFromSearchParams(searchParams: URLSearchParams): void {
  for (const queryParam of GOOGLE_ADS_CLICK_ID_QUERY_PARAMS) {
    searchParams.delete(queryParam);
  }
}

export function isGoogleAdsUrlAttributionEligible({
  cookieHeader,
  globalPrivacyControlHeader,
  countryHeader,
}: {
  cookieHeader?: string;
  globalPrivacyControlHeader?: string | null;
  countryHeader?: string | string[] | null;
}): boolean {
  return (
    globalPrivacyControlHeader !== "1" &&
    getCookieConsentFromHeader(cookieHeader ?? "", "marketing_consent") !== "rejected" &&
    isGoogleAdsRolloutCountryHeader(countryHeader)
  );
}

export function isGoogleAdsServerAttributionEligible({
  cookieHeader,
  globalPrivacyControlHeader,
  countryHeader,
}: {
  cookieHeader?: string;
  globalPrivacyControlHeader?: string | string[] | null;
  countryHeader?: string | string[] | null;
}): boolean {
  // Server redirects can only trust explicit signed-out cookie consent.
  // Authenticated canonical consent is reconciled client-side before capture.
  const globalPrivacyControl =
    Array.isArray(globalPrivacyControlHeader) ?
      globalPrivacyControlHeader[0]
    : globalPrivacyControlHeader;
  return (
    globalPrivacyControl !== "1" &&
    getCookieConsentFromHeader(cookieHeader ?? "", "marketing_consent") === "agreed" &&
    getCookieConsentSourceFromHeader(cookieHeader ?? "", "marketing_consent_source") ===
      "signed_out" &&
    isGoogleAdsRolloutCountryHeader(countryHeader)
  );
}

function isValidGoogleAdsClickId(value: string): boolean {
  return (
    value.trim() === value &&
    value.length > 0 &&
    value.length <= MAX_GOOGLE_ADS_CLICK_ID_LENGTH &&
    !hasControlCharacter(value)
  );
}

function hasControlCharacter(value: string): boolean {
  return Array.from(value).some((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 0x1f || codePoint === 0x7f;
  });
}

function toInternalPath(url: URL): string {
  return `${url.pathname}${url.search}${url.hash}`;
}
